import React, { useCallback, useEffect, useState } from 'react'
import { Badge, Button, Dropdown, Form, Modal, Spinner } from 'react-bootstrap'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { toast } from 'react-toastify'
import { URL as BACKEND_URL } from '../../../gql/client'
import { STORAGE_NAMES } from '../../../helpers/consts'
import { parseId } from '../../../helpers'
import { ModalGoogleLeadsWebhook } from './ModalGoogleLeadsWebhook'
import { ModalFacebookLeadsWebhook } from './ModalFacebookLeadsWebhook'
import { ModalLovableLeadsWebhook } from './ModalLovableLeadsWebhook'
import { getLeadSourceIcon } from '../../leads/helpers/leadSourceIcon'
import { LovableIcon } from '../../../components/icons/LovableIcon'
import { ZapierIcon } from '../../../components/icons/ZapierIcon'

export type Provider = 'google' | 'facebook' | 'lovable'

interface ProviderPerms {
  canView: boolean
  canEdit: boolean
}

interface Props {
  isOpen: boolean
  onClose: () => void
  stage: { id: string; name: string }
  // `change_<provider>_leads_webhook`: sin el permiso el listado es de solo
  // lectura (no crear / editar / borrar) *para ese provider*. El backend exige
  // el mismo codename, así que ocultar el botón no es la única defensa.
  permissions: Record<Provider, ProviderPerms>
}

interface TagOption {
  id: string
  name: string
  color: string
}

interface WebhookSummary {
  id: number
  is_active: boolean
  type_lead: 'PERSONAL' | 'COMERCIAL'
  description: string
  lead_source_name: string | null
  campus_name: string | null
  tags: TagOption[]
  leads_count: number
  last_triggered_at: string | null
}

interface WebhookItem extends WebhookSummary {
  provider: Provider
}

const PROVIDER_LABEL: Record<Provider, string> = {
  google: 'google-leads',
  facebook: 'facebook-leads',
  lovable: 'lovable-leads',
}

function authHeaders(): HeadersInit {
  const token = localStorage.getItem(STORAGE_NAMES.TOKEN) || ''
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  }
}

const emdash = '—'
const colon = ':'
const space = ' '

const wM = defineMessages({
  listTitle: { id: 'leads.webhook.unifiedListTitle', defaultMessage: 'Webhooks' },
  addWebhook: { id: 'leads.webhook.addWebhook', defaultMessage: 'Add webhook' },
  addGoogleWebhook: { id: 'leads.webhook.addGoogleWebhook', defaultMessage: 'Add Google Ads webhook' },
  addZapierWebhook: { id: 'leads.webhook.addZapierWebhook', defaultMessage: 'Add Zapier webhook' },
  addLovableWebhook: { id: 'leads.webhook.addLovableWebhook', defaultMessage: 'Add Lovable webhook' },
  noWebhooks: { id: 'leads.webhook.noWebhooks', defaultMessage: 'No webhooks configured for this stage yet.' },
  leadsReceived: { id: 'leads.webhook.leadsReceived', defaultMessage: 'Leads received' },
  lastTriggered: { id: 'leads.webhook.lastTriggered', defaultMessage: 'Last triggered' },
  errLoadList: { id: 'leads.webhook.errLoadList', defaultMessage: 'Error loading webhooks' },
  errNetworkLoad: { id: 'leads.webhook.errNetworkLoad', defaultMessage: 'Network error loading config' },
  deleteWebhook: { id: 'leads.webhook.deleteWebhook', defaultMessage: 'Delete webhook' },
  deleteConfirm: { id: 'leads.webhook.deleteConfirm', defaultMessage: 'Delete this webhook config?' },
  yesDelete: { id: 'leads.webhook.yesDelete', defaultMessage: 'Yes, delete' },
  webhookDeleted: { id: 'leads.webhook.webhookDeleted', defaultMessage: 'Webhook deleted' },
  errDeleting: { id: 'leads.webhook.errDeleting', defaultMessage: 'Error deleting webhook' },
  errNetworkDelete: { id: 'leads.webhook.errNetworkDelete', defaultMessage: 'Network error deleting webhook' },
  noDescription: { id: 'leads.webhook.noDescription', defaultMessage: 'No description' },
  errToggle: { id: 'leads.webhook.errToggle', defaultMessage: 'Error updating webhook status' },
  errNetworkToggle: { id: 'leads.webhook.errNetworkToggle', defaultMessage: 'Network error updating webhook status' },
})

/** Ícono chico de marca (Facebook/Instagram/Google/WhatsApp) según el lead_source
 *  configurado en ese webhook puntual — independiente del logo del provider. */
function LeadSourceMark({ leadSourceName }: { leadSourceName: string | null }) {
  const brandIcon = getLeadSourceIcon(leadSourceName)
  if (!brandIcon) return null
  return <i className={`bi ${brandIcon}`} />
}

/** Logo grande del mecanismo del webhook (columna izquierda de la card) — Google,
 *  Zapier o Lovable — no confundir con el ícono de marca del lead_source. */
function ProviderLogo({ provider }: { provider: Provider }) {
  if (provider === 'lovable') return <LovableIcon size={36} />
  if (provider === 'google') return <i className='bi bi-google text-danger' style={{ fontSize: '2rem' }} />
  return <ZapierIcon size={36} />
}

export const ModalLeadsWebhookList: React.FC<Props> = ({ isOpen, onClose, stage, permissions }) => {
  const intl = useIntl()
  const stageNumericId = Number(parseId(stage.id))

  const [items, setItems] = useState<WebhookItem[]>([])
  const [loading, setLoading] = useState(false)
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null)
  const [deletingId, setDeletingId] = useState<number | null>(null)

  const [isConfigOpen, setIsConfigOpen] = useState(false)
  const [configProvider, setConfigProvider] = useState<Provider>('facebook')
  const [editingConfigId, setEditingConfigId] = useState<number | null>(null)

  const viewableProviders = (Object.keys(permissions) as Provider[]).filter((p) => permissions[p].canView)
  const addableProviders = (Object.keys(permissions) as Provider[]).filter((p) => permissions[p].canEdit)

  const loadList = useCallback(async () => {
    if (!stageNumericId || viewableProviders.length === 0) return
    setLoading(true)
    try {
      const results = await Promise.all(
        viewableProviders.map(async (provider) => {
          const res = await fetch(
            `${BACKEND_URL}/api/${PROVIDER_LABEL[provider]}/webhook/${stageNumericId}/`,
            { headers: authHeaders() }
          )
          if (!res.ok) return [] as WebhookItem[]
          const data: { results: WebhookSummary[] } = await res.json()
          return (data.results || []).map((r) => ({ ...r, provider }))
        })
      )
      setItems(results.flat())
    } catch {
      toast.error(intl.formatMessage(wM.errNetworkLoad))
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stageNumericId, intl])

  useEffect(() => {
    if (!isOpen) {
      setItems([])
      setConfirmDeleteId(null)
      setIsConfigOpen(false)
      setEditingConfigId(null)
      return
    }
    loadList()
  }, [isOpen, loadList])

  const openCreate = (provider: Provider) => {
    setConfigProvider(provider)
    setEditingConfigId(null)
    setIsConfigOpen(true)
  }

  const openEdit = (item: WebhookItem) => {
    setConfigProvider(item.provider)
    setEditingConfigId(item.id)
    setIsConfigOpen(true)
  }

  const closeConfig = () => {
    setIsConfigOpen(false)
    setEditingConfigId(null)
    loadList()
  }

  const handleDelete = async (item: WebhookItem) => {
    setDeletingId(item.id)
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/${PROVIDER_LABEL[item.provider]}/webhook/config/${item.id}/delete/`,
        { method: 'DELETE', headers: authHeaders() }
      )
      if (res.ok) {
        toast.success(intl.formatMessage(wM.webhookDeleted))
        setConfirmDeleteId(null)
        loadList()
      } else {
        toast.error(intl.formatMessage(wM.errDeleting))
      }
    } catch {
      toast.error(intl.formatMessage(wM.errNetworkDelete))
    } finally {
      setDeletingId(null)
    }
  }

  const [togglingId, setTogglingId] = useState<number | null>(null)

  const handleToggleActive = async (item: WebhookItem) => {
    setTogglingId(item.id)
    // Optimistic: el toggle se siente instantáneo aunque la respuesta tarde.
    setItems((prev) =>
      prev.map((i) => (i.id === item.id && i.provider === item.provider ? { ...i, is_active: !i.is_active } : i))
    )
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/${PROVIDER_LABEL[item.provider]}/webhook/config/${item.id}/toggle/`,
        { method: 'POST', headers: authHeaders() }
      )
      if (!res.ok) {
        toast.error(intl.formatMessage(wM.errToggle))
        loadList()
      }
    } catch {
      toast.error(intl.formatMessage(wM.errNetworkToggle))
      loadList()
    } finally {
      setTogglingId(null)
    }
  }

  return (
    <>
      <Modal
        show={isOpen && !isConfigOpen}
        onHide={onClose}
        size='lg'
        centered
        className='webhook-modal-dialog'
        backdropClassName='webhook-modal-backdrop'
      >
        <Modal.Header closeButton>
          <Modal.Title className='d-flex align-items-center gap-2'>
            <i className='bi bi-plug fs-5' />
            {intl.formatMessage(wM.listTitle)} {emdash} {stage.name}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {loading ? (
            <div className='text-center py-5'>
              <Spinner animation='border' />
            </div>
          ) : (
            <>
              {items.length === 0 ? (
                <p className='text-muted text-center py-4 mb-0'>{intl.formatMessage(wM.noWebhooks)}</p>
              ) : (
                <div className='d-flex flex-column gap-2'>
                  {items.map((item) => {
                    const canEdit = permissions[item.provider].canEdit
                    return (
                      <div key={`${item.provider}-${item.id}`} className='border rounded p-3'>
                        <div className='d-flex align-items-center gap-3'>
                          {/* Logo del provider, centrado verticalmente respecto a toda la card */}
                          <div className='d-flex align-items-center justify-content-center flex-shrink-0' style={{ width: 40 }}>
                            <ProviderLogo provider={item.provider} />
                          </div>

                          {/* Contenido central */}
                          <div className='flex-grow-1 min-w-0'>
                            <span className='fw-semibold text-truncate d-block'>
                              {item.description || intl.formatMessage(wM.noDescription)}
                            </span>

                            <div className='d-flex align-items-center flex-wrap gap-1 small text-muted mt-1'>
                              {item.tags.map((tag) => (
                                <span
                                  key={tag.id}
                                  className='badge rounded-pill'
                                  style={{
                                    background: tag.color || '#6c757d',
                                    color: '#fff',
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                  }}
                                >
                                  {tag.name}
                                </span>
                              ))}
                              {item.tags.length > 0 && <span>{emdash}</span>}
                              <LeadSourceMark leadSourceName={item.lead_source_name} />
                              <span>
                                {item.lead_source_name || <FormattedMessage id='leads.form.leadSource' defaultMessage='Lead source' />}
                              </span>
                              {item.campus_name && <span>{emdash} {item.campus_name}</span>}
                            </div>

                            <div className='d-flex gap-3 small text-muted mt-1'>
                              <span>
                                {intl.formatMessage(wM.leadsReceived)}
                                {colon}
                                {space}
                                {item.leads_count}
                              </span>
                              <span>
                                {intl.formatMessage(wM.lastTriggered)}
                                {colon}
                                {space}
                                {item.last_triggered_at ? new Date(item.last_triggered_at).toLocaleString() : emdash}
                              </span>
                            </div>
                          </div>

                          {/* Acciones — badge/switch arriba, edit/delete abajo, alineados al borde derecho */}
                          <div className='d-flex flex-column align-items-end gap-2 flex-shrink-0 align-self-start'>
                            <div className='d-flex align-items-center gap-2'>
                              <Badge bg={item.is_active ? 'success' : 'secondary'}>
                                {item.is_active
                                  ? intl.formatMessage({ id: 'common.status.active', defaultMessage: 'Active' })
                                  : intl.formatMessage({ id: 'common.status.inactive', defaultMessage: 'Inactive' })}
                              </Badge>
                              <Form.Check
                                type='switch'
                                id={`webhook-active-${item.provider}-${item.id}`}
                                checked={item.is_active}
                                disabled={!canEdit || togglingId === item.id}
                                onChange={() => handleToggleActive(item)}
                                className='mb-0'
                              />
                            </div>

                            <div className='d-flex align-items-center gap-1'>
                              <Button variant='outline-secondary' size='sm' onClick={() => openEdit(item)}>
                                <i className={`bi ${canEdit ? 'bi-pencil' : 'bi-eye'}`} />
                              </Button>

                              {!canEdit ? null : confirmDeleteId === item.id ? (
                                <div className='d-flex align-items-center gap-2'>
                                  <small className='text-danger'>{intl.formatMessage(wM.deleteConfirm)}</small>
                                  <Button
                                    variant='danger'
                                    size='sm'
                                    onClick={() => handleDelete(item)}
                                    disabled={deletingId === item.id}
                                  >
                                    {deletingId === item.id ? (
                                      <Spinner size='sm' animation='border' />
                                    ) : (
                                      intl.formatMessage(wM.yesDelete)
                                    )}
                                  </Button>
                                  <Button variant='link' size='sm' onClick={() => setConfirmDeleteId(null)}>
                                    <FormattedMessage id='common.action.cancel' defaultMessage='Cancel' />
                                  </Button>
                                </div>
                              ) : (
                                <Button
                                  variant='outline-danger'
                                  size='sm'
                                  title={intl.formatMessage(wM.deleteWebhook)}
                                  onClick={() => setConfirmDeleteId(item.id)}
                                >
                                  <i className='bi bi-trash' />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              {addableProviders.length > 0 && (
                <Dropdown className='mt-3'>
                  <Dropdown.Toggle variant='outline-primary' id='add-webhook-dropdown'>
                    <i className='bi bi-plus-lg me-1' />
                    {intl.formatMessage(wM.addWebhook)}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {addableProviders.includes('google') && (
                      <Dropdown.Item onClick={() => openCreate('google')}>
                        <i className='bi bi-google text-danger me-2' />
                        {intl.formatMessage(wM.addGoogleWebhook)}
                      </Dropdown.Item>
                    )}
                    {addableProviders.includes('facebook') && (
                      <Dropdown.Item onClick={() => openCreate('facebook')}>
                        <ZapierIcon size={14} className='me-2' />
                        {intl.formatMessage(wM.addZapierWebhook)}
                      </Dropdown.Item>
                    )}
                    {addableProviders.includes('lovable') && (
                      <Dropdown.Item onClick={() => openCreate('lovable')}>
                        <LovableIcon size={14} className='me-2' />
                        {intl.formatMessage(wM.addLovableWebhook)}
                      </Dropdown.Item>
                    )}
                  </Dropdown.Menu>
                </Dropdown>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant='secondary' onClick={onClose}>
            <FormattedMessage id='common.action.close' defaultMessage='Close' />
          </Button>
        </Modal.Footer>
      </Modal>

      {isConfigOpen &&
        (configProvider === 'google' ? (
          <ModalGoogleLeadsWebhook
            isOpen={isConfigOpen}
            onClose={closeConfig}
            stage={stage}
            configId={editingConfigId}
            onChanged={loadList}
            canEdit={permissions.google.canEdit}
          />
        ) : configProvider === 'lovable' ? (
          <ModalLovableLeadsWebhook
            isOpen={isConfigOpen}
            onClose={closeConfig}
            stage={stage}
            configId={editingConfigId}
            onChanged={loadList}
            canEdit={permissions.lovable.canEdit}
          />
        ) : (
          <ModalFacebookLeadsWebhook
            isOpen={isConfigOpen}
            onClose={closeConfig}
            stage={stage}
            configId={editingConfigId}
            onChanged={loadList}
            canEdit={permissions.facebook.canEdit}
          />
        ))}
    </>
  )
}
